import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/* =========================================================================
   ScrollToHash
   Rola a página até o elemento indicado por "#id" na URL, mesmo quando a
   navegação vem de outra rota via <Link>.

   Duas diferenças importantes em relação a uma versão mais simples
   (baseada só em requestAnimationFrame):

   1. Tenta encontrar o elemento várias vezes por um curto período — isso
      cobre casos em que o layout da página de destino ainda está se
      ajustando (imagens, fontes, animações de entrada) no exato instante
      em que o React termina de montar a rota nova.

   2. Rola manualmente (window.scrollTo) descontando a altura do header
      fixo (--header-h), em vez de usar scrollIntoView puro — assim a
      seção não fica escondida atrás do header.

   Uso: renderize <ScrollToHash /> uma única vez, dentro do <BrowserRouter>,
   como irmão do <Routes>. Substitua qualquer outro componente que faça
   scroll-to-top nas trocas de rota (ex.: ScrollToTop) por este, para não
   haver dois efeitos de scroll disputando a posição da página ao mesmo
   tempo — se o antigo ScrollToTop ainda estiver montado em outro lugar do
   projeto (por exemplo, dentro do Header), remova-o de lá também.
   ========================================================================= */

const HEADER_OFFSET = 96; // um pouco maior que --header-h (72px) para dar respiro

export default function ScrollToHash() {
  const { hash, pathname, key } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const id = hash.replace("#", "");
    let attempts = 0;
    const maxAttempts = 40; // ~2s tentando a cada 50ms
    let timeoutId;
    let cancelled = false;

    const tryScroll = () => {
      if (cancelled) return;

      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
        window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        timeoutId = setTimeout(tryScroll, 50);
      }
    };

    tryScroll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, pathname, key]);

  return null;
}
